import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useToast } from "./ui/Toast";
import { AddButton } from "./buttons/AddButton";
import { Plan2PlateDrawer, usePlan2PlateDisclosure } from "./Plan2PlateDrawer";
import { CreateItemDrawer } from "./CreateItemDrawer";
import { SearchInput } from "./SearchInput";
import { Draft } from "immer";
import { useImmer } from "use-immer";
import { usePerformSearch } from "../utils/searchUtils";
import {
  createIntakesFromEntity,
  FoodDto,
  getLastestIntakes,
  getLastestMeals,
  getPurchase,
  loadFood as loadFoodRequest,
  MealSourceDto,
  MealSourceIdDto,
  PurchaseDto,
  searchMealSource,
} from "../api";
import {
  IntakeWithEditedFlags,
  JustIntake,
  MealWithIntakes,
  PlanCell,
  PlanCellCoordinatesWithOptionalPerson,
  PlanCellWithIndex,
} from "../data/plan";
import InfiniteScroll from "react-infinite-scroll-component";
import { computeResultType, getMealSourceKey } from "../utils/mealSourceUtils";
import {
  computePlanCellCoordinates,
  computePlanCellCoordinatesFromPlanCellWithIndex,
  convertToNewPlanCellWithIndex,
  findById,
  formatPlanCellDescription,
  getStrictMealKeyForMeal,
  isNullIndex,
  mapPlanCellToNutriValues,
  nutriValuesFromIntake,
  nutriValuesFromMealIngredient,
  nutriValueSumReducer,
  rebuildPlanCells,
} from "../utils/planUtils";
import { DrawerHistoryContext, MealPlannerContext } from "../contexts";
import { usePersonsAsync } from "../queries/queries";
import {
  invalidatePurchases,
  useFoodsByIdsState,
  useJustIntakePurchasesState,
  useMealPurchasesState,
} from "../queries/caches";
import { collectFoodIds, collectFoodUnitIds } from "../utils/foodIdUtils";
import {
  findMatchingUnit,
  formatFoodDescription,
  getFoodMaybe,
  recalculateAmountByUnitChange,
} from "../utils/foodUtils";
import { useQueryClient } from "@tanstack/react-query";
import { DetailSearchResult, isRecent } from "./DetailSearchResult";
import { mealInds } from "../utils/mealIndUtils";
import {
  addDays,
  formatIsoString,
  isTodayOrPrevious,
  parseDate,
} from "../utils/dateUtils";
import { isDeleted } from "../utils/editUtils";
import { FastIntakeButton } from "./buttons/FastIntakeButton";
import { deepEquals } from "../utils/equalUtils";
import { showValidationMessage } from "./showValidationMessage";
import { createPlanCells } from "../utils/createMeal";
import { emptyNutriValue, NutriValues } from "../data/nutriValues";
import { Card } from "./ui/Card";
import { MacroBar } from "./ui/MacroBar";
import { EditDishDrawer } from "./EditDishDrawer";
import { EditFoodDrawer } from "./EditFoodDrawer";
import { EditPurchaseDrawer } from "./EditPurchaseDrawer";
import { Purchase } from "../data/purchase";
import {
  DishModification,
  DishTag,
  StepIngredientModification,
} from "../data/dishModification";
import Food from "../data/food";
import { loadDishModification } from "../loaders";
import { UnitAmount } from "./UnitAmount";
import { FoodUnit } from "../data/foodUnit";
import { MealIngredient } from "../data/mealWithIngredients";
import { formatPurchaseDescription } from "../utils/purchaseUtils";
import { SmallIconButton } from "./SmallIconButton";
import { Button } from "./ui/Button";
import { NutriRow } from "./ui/NutriRow";
import { NutriFilterDrawer } from "./NutriFilterDrawer";
import {
  emptyNutriSearchFilter,
  formatNutriFilterRange,
  isNutriFilterActive,
  mealIndicatorLabels,
  MealIndicator,
  NutriSearchFilter,
} from "../data/search";
import { IngredientRestoreRow, IngredientRow } from "./dish/IngredientRow";
import { PlanCellCard } from "./plan/PlanCellCard";
import { PlanCoordinateHeader } from "./PlanCoordinateHeader";
import { cn } from "../lib/utils";

export type PlanMode = "plan" | "intake";

export type NutriFilterPillKey =
  | "indicators"
  | "tags"
  | "kcal"
  | "fat"
  | "carbs"
  | "protein";

function FilterPill(props: {
  label: string;
  value?: string;
  onRemove: () => void;
  onOpen: () => void;
}) {
  const active = props.value != null;
  return (
    <button
      type="button"
      data-cy="search-filter-pill"
      aria-label={
        active ? `${props.label}-Filter entfernen` : `${props.label} filtern`
      }
      onClick={active ? props.onRemove : props.onOpen}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-pill border px-2.5 py-1 text-xs font-bold transition",
        active
          ? "border-gold bg-gold/10 text-ink"
          : "border-line bg-surface-card text-ink-muted hover:bg-surface",
      )}
    >
      <span>{props.label}</span>
      {props.value != null && <span className="text-gold">{props.value}</span>}
      {active && (
        <span aria-hidden="true" className="text-gold">
          ×
        </span>
      )}
    </button>
  );
}

type StepInfo = { stepIndex: number; stepText?: string | null };

/**
 * Gelöschte Zutaten werden (statt sie zu entfernen) mit ihrer ursprünglichen
 * Position zwischengespeichert, damit sie an derselben Stelle und mit derselben
 * Menge wiederhergestellt werden können.
 */
type RemovedIngredient = { index: number; ingredient: MealIngredient };
type MealWithRemoved = MealWithIntakes & {
  removedIngredients?: RemovedIngredient[];
};

type SiWithStep = StepIngredientModification & {
  stepPos: number;
  stepText?: string | null;
};

interface SearchForPlanCellDrawerProps {
  onClose: () => void;
  onSubmit: () => void;
  open: boolean;
  planCellCoordinates: PlanCellCoordinatesWithOptionalPerson;
  planMode: "plan" | "intake";
  hideSearch?: boolean;
}

/**
 * Der komplette Zustand des Drawers lebt in diesem Hook – also **oberhalb** der
 * Slot-`QueryBoundary`s im `Plan2PlateDrawer`. Dadurch geht bei einem Suspense
 * der Slots (die dort ihre Daten-Hooks haben) kein State verloren und der
 * Drawer bleibt geöffnet (kein Flackern).
 */
function useSearchState(props: SearchForPlanCellDrawerProps) {
  // ─── Lokale neue PlanCells (in dieser Sitzung hinzugefügt) ─────────────────
  const [planCells, updatePlanCells] = useImmer<PlanCell[]>([]);
  // ─── Editierbare lokale Kopie der bereits geplanten Mahlzeiten ─────────────
  const [initialTableData, setInitialTableData] =
    useState<PlanCellWithIndex[]>();
  const [localTableData, updateLocalTableData] = useImmer<PlanCellWithIndex[]>(
    [],
  );
  const [pristine, setPristine] = useState(true);
  useEffect(() => {
    if (
      initialTableData?.length &&
      !deepEquals(initialTableData, localTableData)
    ) {
      setPristine(false);
    }
  }, [initialTableData, localTableData, setPristine]);
  // ─── Aufgeklappte Karten ────────────────────────────────────────────────────
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  // Ref: Länge planCells vor letztem Render – für Auto-Expand neuer Einträge
  const prevPlanCellsLengthRef = useRef(0);

  // ─── Suche ─────────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, updateSearchResults] = useImmer<MealSourceDto[]>([]);
  const [searchResultCount, setSearchResultCount] = useState(0);
  // Filter: Typen (Indicators), Tags und Nährwert-Grenzen
  const [indicators, setIndicators] = useState<MealIndicator[]>([]);
  const [tags, setTags] = useState<DishTag[]>([]);
  const [nutriFilter, setNutriFilter] = useState<NutriSearchFilter>(
    emptyNutriSearchFilter,
  );
  // Wird bei jedem angewendeten Filter erhöht, damit die Suche auch bei
  // unveränderten Werten neu angestoßen wird.
  const [searchTrigger, setSearchTrigger] = useState(0);
  // true, sobald eine Suche ausgelöst wurde (Enter oder „Ergebnisse anzeigen“).
  // Steuert Pagination/Anzeige, damit die Vorschau nicht in eine Textsuche kippt.
  const [hasSearched, setHasSearched] = useState(false);

  // ─── Contexts ──────────────────────────────────────────────────────────────
  const queryClient = useQueryClient();
  const loadPurchase = async () => {
    await invalidatePurchases(queryClient);
  };
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const { submitPlanCells, onConsume, getTableDataForCoordinates } =
    useContext(MealPlannerContext);
  const toast = useToast();

  // tableData: live aus dem Planer (nur für Initialisierung / hasMeals-Check)
  const tableData = getTableDataForCoordinates(props.planCellCoordinates);

  // ─── Drawer-Disclosures ────────────────────────────────────────────────────
  const editDishDisclosure = usePlan2PlateDisclosure(drawerHistoryContext);
  const editFoodDisclosure = usePlan2PlateDisclosure(drawerHistoryContext);
  const editPurchaseDisclosure = usePlan2PlateDisclosure(drawerHistoryContext);
  const createItemDisclosure = usePlan2PlateDisclosure(drawerHistoryContext);
  const filterDisclosure = usePlan2PlateDisclosure(drawerHistoryContext);

  // ─── Pencil-Zustand ─────────────────────────────────────────────────────────
  const [editingSource, setEditingSource] = useState<{
    isNew: boolean;
    index: number;
  } | null>(null);
  // true = Stift an einem Suchergebnis (nur Entität bearbeiten, kein Plan-Cell)
  const [editResultOnly, setEditResultOnly] = useState(false);
  const [dishModification, setDishModification] = useState<
    DishModification | undefined
  >();
  const [foodModification, setFoodModification] = useState<Food | undefined>();
  const [purchaseModification, setPurchaseModification] = useState<
    Purchase | undefined
  >();
  const [loadedPlanDishes, setLoadedPlanDishes] = useState<
    Record<number, DishModification>
  >({});

  const { performSearch, fetchMoreData } = usePerformSearch(
    props.planCellCoordinates.personId,
    nutriFilter,
    searchTerm,
    searchResults,
    updateSearchResults,
    setSearchResultCount,
    searchMealSource,
    10,
    indicators,
    tags,
  );

  // Ergebnisanzahl-Vorschau für den Filter-Drawer (pageSize 0 ⇒ nur Count).
  const countNutriResults = useCallback(
    async (
      resultIndicators: MealIndicator[],
      resultTags: DishTag[],
      filter: NutriSearchFilter,
    ) => {
      const response = await searchMealSource({
        body: {
          searchText: searchTerm,
          indicators:
            resultIndicators.length > 0 ? resultIndicators : undefined,
          tags: resultTags.length > 0 ? resultTags : undefined,
          fromIndex: 0,
          pageSize: 0,
          nutriSearchFilter: {
            ...filter,
            activePerson: props.planCellCoordinates.personId,
          },
        },
      });
      return response.data.count;
    },
    [searchTerm, props.planCellCoordinates.personId],
  );

  const applyNutriFilter = (
    resultIndicators: MealIndicator[],
    resultTags: DishTag[],
    filter: NutriSearchFilter,
  ) => {
    setIndicators(resultIndicators);
    setTags(resultTags);
    setNutriFilter(filter);
    setSearchTrigger((trigger) => trigger + 1);
  };

  /** Entfernt einen einzelnen Filter (Klick auf die zugehörige Pill). */
  const clearNutriFilter = (key: NutriFilterPillKey) => {
    switch (key) {
      case "indicators":
        setIndicators([]);
        break;
      case "tags":
        setTags([]);
        break;
      case "kcal":
        setNutriFilter((filter) => ({
          ...filter,
          minKcal: 0,
          maxKcal: undefined,
        }));
        break;
      case "fat":
        setNutriFilter((filter) => ({
          ...filter,
          minFat: 0,
          maxFat: undefined,
        }));
        break;
      case "carbs":
        setNutriFilter((filter) => ({
          ...filter,
          minCarbs: 0,
          maxCarbs: undefined,
        }));
        break;
      case "protein":
        setNutriFilter((filter) => ({
          ...filter,
          minProtein: 0,
          maxProtein: undefined,
        }));
        break;
    }
    setSearchTrigger((trigger) => trigger + 1);
  };

  // Ergebnisse erst laden, sobald ein Suchbegriff eingegeben wurde. Filter-
  // Änderungen ohne Begriff aktualisieren nur den Count im Drawer.
  const isInitialSearch = useRef(true);
  useEffect(() => {
    if (isInitialSearch.current) {
      isInitialSearch.current = false;
      return;
    }
    if (props.open) {
      setHasSearched(true);
      performSearch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicators.join(","), searchTrigger]);

  // ─── Initialisierung / Reset beim Öffnen/Schließen ─────────────────────────
  useEffect(() => {
    if (!props.open) {
      updatePlanCells([]);
      updateLocalTableData([]);
      setExpandedCards(new Set());
      setSearchTerm("");
      setSearchResultCount(0);
      updateSearchResults([]);
      prevPlanCellsLengthRef.current = 0;
      setLoadedPlanDishes({});
      setIndicators([]);
      setTags([]);
      setNutriFilter(emptyNutriSearchFilter);
      setHasSearched(false);
      return;
    }
    // Snapshot der bestehenden Mahlzeiten beim Öffnen;
    // unvollständige sofort aufklappen (fix #5)
    const snapshot = getTableDataForCoordinates(
      props.planCellCoordinates,
    ).filter((pc) =>
      props.planMode === "plan"
        ? !isMealConsumed(pc.planCell) && pc.planCell.indicator !== "JustIntake"
        : pc.planCell.indicator !== "JustIntake",
    );
    updateLocalTableData(snapshot);
    setInitialTableData(snapshot);
    setPristine(true);
    const initialExpanded = new Set<string>();
    snapshot.forEach((pc, i) => {
      if (isPlanCellIncomplete(pc.planCell))
        initialExpanded.add(`existing-${i}`);
    });
    setExpandedCards(initialExpanded);

    if (!!props.planCellCoordinates.personId) {
      const endDate = formatIsoString(
        addDays(parseDate(props.planCellCoordinates.date), -1),
      );
      const startDate = formatIsoString(addDays(parseDate(endDate), -7));
      const query = {
        mealInd: props.planCellCoordinates.mealInd,
        mealDateStart: startDate,
        mealDateEnd: endDate,
        personId: props.planCellCoordinates.personId!,
      };
      const request =
        props.planMode == "plan"
          ? getLastestMeals({ query })
          : getLastestIntakes({ query });
      request.then((r) => {
        setSearchResultCount(r.data.count);
        updateSearchResults(r.data.searchResults);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // ─── Auto-Expand: nur unvollständige neue Cards aufklappen ──────────────────
  useEffect(() => {
    const newLength = planCells.length;
    const difference = newLength - prevPlanCellsLengthRef.current;
    const expanded: string[] = [];
    for (let i = 1; i <= difference; i++) {
      const newIndex = newLength - i;
      const newCell = planCells[newIndex];
      if (newCell && isPlanCellIncomplete(newCell)) {
        expanded.push(`new-${newIndex}`);
      }
    }
    if (expanded.length) {
      setExpandedCards((prev) => new Set([...prev, ...expanded]));
    }
    prevPlanCellsLengthRef.current = newLength;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planCells.length]);

  // ─── Berechnungen ──────────────────────────────────────────────────────────
  const mealIndDescription =
    mealInds[props.planCellCoordinates.mealInd]?.name || "";

  const remainingTableData = useMemo(() => {
    const tableDataForCoordinates = getTableDataForCoordinates({
      ...props.planCellCoordinates,
      index: -1,
    });
    return tableDataForCoordinates.filter((td) => {
      const tableDataCoordinates =
        computePlanCellCoordinatesFromPlanCellWithIndex(td);
      return !localTableData.find((ltd) => {
        const ltdCoordinates =
          computePlanCellCoordinatesFromPlanCellWithIndex(ltd);
        return ltdCoordinates.index === tableDataCoordinates.index;
      });
    });
  }, [getTableDataForCoordinates, localTableData, props.planCellCoordinates]);

  const newPlanCellCount = planCells.filter((pc) => !isDeleted(pc)).length;
  const activePlanCellCount =
    newPlanCellCount +
    localTableData.filter((pc) => !isDeleted(pc.planCell)).length;

  // „Für alle planen": personId ist nicht gesetzt
  const isForAll = !props.planCellCoordinates.personId;

  // ─── Vollständigkeitsprüfung ────────────────────────────────────────────────
  function isPlanCellIncomplete(pc: PlanCell): boolean {
    if (isDeleted(pc)) return false;
    if (pc.indicator === "JustIntake") {
      return pc.amount <= 0 || isNullIndex(pc.purchaseUnitId);
    }
    if (props.planMode === "plan") {
      return pc.meal.mealIngredients.some(
        (mi) =>
          mi.amount <= 0 ||
          (isNullIndex(mi.foodUnitId) && isNullIndex(mi.purchaseUnitId)),
      );
    }
    if (!pc.intakes || pc.intakes.length === 0) return false;
    return pc.intakes
      .filter((i) => !isDeleted(i))
      .some(
        (intake) => intake.amount <= 0 || isNullIndex(intake.purchaseUnitId),
      );
  }

  function isMealConsumed(planCell: PlanCell): boolean {
    if (planCell.indicator !== "MealWithIntakes") return false;
    const intakes = planCell.intakes || [];
    const activeIntakes = intakes.filter(
      (i) => !isDeleted(i) && (i.amount || 0) > 0,
    );
    return activeIntakes.length > 0;
  }

  // ─── Aktionen ──────────────────────────────────────────────────────────────
  async function addPlanCell(
    planCellCoordinates: PlanCellCoordinatesWithOptionalPerson,
    mealSourceId: MealSourceIdDto,
  ) {
    const requestObj = { planCellCoordinates, mealSourceId };

    function pushIfNotExists(
      draft: Array<Draft<PlanCell>>,
      newCells: PlanCell[],
    ) {
      const key = (p: PlanCell) =>
        p.indicator == "MealWithIntakes"
          ? getStrictMealKeyForMeal(p.meal)
          : { purchaseId: p.purchaseUnitPurchaseId };
      for (const cell of newCells) {
        if (!draft.find((d) => deepEquals(key(d as PlanCell), key(cell))))
          draft.push(cell);
      }
    }

    if (props.planMode == "plan") {
      const cells = await createPlanCells(requestObj);
      updatePlanCells((draft) => pushIfNotExists(draft, cells));
    } else {
      const intakes = (await createIntakesFromEntity({ body: requestObj }))
        .data;
      const newCells: PlanCell[] = intakes.map((i) => ({
        indicator: "JustIntake",
        ...i,
      }));
      updatePlanCells((draft) => pushIfNotExists(draft, newCells));
    }
  }

  const deleteNewCell = (index: number) => {
    updatePlanCells((draft) => {
      draft.splice(index, 1);
    });
  };

  const deleteExistingCell = (index: number) => {
    updateLocalTableData((draft) => {
      draft[index].planCell.deleted = true;
    });
  };

  const handleSave = () => {
    const allActive = [
      ...planCells.filter((pc) => !isDeleted(pc)),
      ...localTableData
        .map(({ planCell }) => planCell)
        .filter((pc) => !isDeleted(pc)),
    ];
    const incomplete = allActive.filter(isPlanCellIncomplete);
    if (incomplete.length > 0) {
      showValidationMessage(
        toast,
        "Bitte Menge und Einheit für alle Einträge angeben.",
      );
      return;
    }
    submitPlanCells(localTableData);
    submitPlanCells(planCells.map((pc) => ({ planCell: pc, index: -1 })));
    props.onSubmit();
  };

  const hasMeals = tableData.find(
    (pC) => pC.planCell?.indicator == "MealWithIntakes",
  );
  const saveLabel =
    props.planMode == "plan" ? "Plan speichern" : "Verzehr speichern";

  return {
    props,
    planCells,
    updatePlanCells,
    localTableData,
    updateLocalTableData,
    pristine,
    setPristine,
    initialTableData,
    expandedCards,
    setExpandedCards,
    searchTerm,
    setSearchTerm,
    searchResults,
    updateSearchResults,
    searchResultCount,
    editingSource,
    setEditingSource,
    editResultOnly,
    setEditResultOnly,
    dishModification,
    setDishModification,
    foodModification,
    setFoodModification,
    purchaseModification,
    setPurchaseModification,
    loadedPlanDishes,
    setLoadedPlanDishes,
    editDishDisclosure,
    editFoodDisclosure,
    editPurchaseDisclosure,
    createItemDisclosure,
    filterDisclosure,
    indicators,
    setIndicators,
    tags,
    setTags,
    nutriFilter,
    applyNutriFilter,
    clearNutriFilter,
    countNutriResults,
    nutriFilterActive:
      indicators.length > 0 ||
      tags.length > 0 ||
      isNutriFilterActive(nutriFilter),
    hasSearched,
    setHasSearched,
    performSearch,
    fetchMoreData,
    mealIndDescription,
    remainingTableData,
    newPlanCellCount,
    activePlanCellCount,
    isForAll,
    isPlanCellIncomplete,
    isMealConsumed,
    addPlanCell,
    deleteNewCell,
    deleteExistingCell,
    handleSave,
    hasMeals,
    saveLabel,
    tableData,
    toast,
    drawerHistoryContext,
    onConsume,
    submitPlanCells,
    getTableDataForCoordinates,
    loadPurchase,
  };
}

type SearchState = ReturnType<typeof useSearchState>;

/**
 * Suspender Daten-Hook – wird in Body **und** Footer aufgerufen (dieselben
 * Queries, daher gecacht/gebündelt). Liegt bewusst unterhalb der Slot-Boundary.
 */
function useSearchNutri(state: SearchState) {
  const { planCells, localTableData, loadedPlanDishes, remainingTableData } =
    state;
  // Zellen, deren Nährwerte `getNutri` berechnet: neue + lokal editierte UND die
  // übrigen Zeilen des Slots (`remainingTableData`: bereits verzehrt / JustIntake).
  // Würden diese fehlen, mangelte es `nutriValuesFromFood`/`...Intake` an
  // Foods/Purchases → stille 0 bzw. Lookup-Miss.
  const mealCells = useMemo(
    () =>
      [
        ...planCells,
        ...localTableData.map(({ planCell }) => planCell),
        ...remainingTableData.map(({ planCell }) => planCell),
      ].filter(
        (planCell): planCell is MealWithIntakes =>
          planCell.indicator === "MealWithIntakes",
      ),
    [planCells, localTableData, remainingTableData],
  );
  const mealPurchasesState = useMealPurchasesState(
    useMemo(() => mealCells.map((planCell) => planCell.meal), [mealCells]),
  );
  const remainingJustIntakes = useMemo(
    () =>
      remainingTableData
        .map(({ planCell }) => planCell)
        .filter(
          (planCell): planCell is JustIntake =>
            planCell.indicator !== "MealWithIntakes",
        ),
    [remainingTableData],
  );
  const remainingIntakePurchasesState = useJustIntakePurchasesState(
    remainingJustIntakes,
  );
  const purchasesData = useMemo(() => {
    const byId = new Map<number, PurchaseDto>();
    [
      ...mealPurchasesState.purchases,
      ...remainingIntakePurchasesState.purchases,
    ].forEach((purchase) => byId.set(purchase.id, purchase));
    return [...byId.values()];
  }, [mealPurchasesState.purchases, remainingIntakePurchasesState.purchases]);
  const purchasesLoading =
    mealPurchasesState.isLoading || remainingIntakePurchasesState.isLoading;
  const foodIds = useMemo(
    () =>
      collectFoodIds(
        mealCells.map((planCell) => planCell.meal),
        purchasesData,
      ),
    [mealCells, purchasesData],
  );
  const foodUnitIds = useMemo(() => {
    const ids = new Set<number>();
    Object.values(loadedPlanDishes).forEach((dish) =>
      collectFoodUnitIds(dish.steps).forEach((id) => ids.add(id)),
    );
    mealCells.forEach((planCell) =>
      planCell.meal.mealIngredients.forEach((mi) => {
        if (mi.foodUnitId) ids.add(mi.foodUnitId);
      }),
    );
    return [...ids];
  }, [loadedPlanDishes, mealCells]);
  const { data: foodsData, isLoading: foodsLoading } = useFoodsByIdsState(
    foodIds,
    foodUnitIds,
  );

  // Während Foods/Purchases noch laden, ist das jeweilige Array unvollständig.
  // Es darf dann nicht als „leer" (nichts gefunden) gelten: `findById` würde
  // sonst fälschlich Lookup-Misses melden. Deshalb `undefined` bis alles geladen
  // ist – die Aufrufer behandeln das als „noch keine Daten".
  const lookupsLoading = foodsLoading || purchasesLoading;
  const foods = lookupsLoading ? undefined : foodsData;
  const purchases = lookupsLoading ? undefined : purchasesData;

  const getNutri = (cells: PlanCell[]) => {
    if (!foods || !purchases) return cells.map(() => emptyNutriValue);
    return cells.map((pc) => {
      const vals = mapPlanCellToNutriValues(pc, foods, purchases);
      return vals.length > 0
        ? vals.reduce(nutriValueSumReducer)
        : emptyNutriValue;
    });
  };

  const planCellNutriList = useMemo(
    () => getNutri(planCells),
    [planCells, foods, purchases],
  );
  const tableDataNutriList = useMemo(
    () => getNutri(localTableData.map(({ planCell }) => planCell)),
    [localTableData, foods, purchases],
  );

  const totalNutri = useMemo(() => {
    const active = [
      ...planCells.map((pc, i) => ({ pc, n: planCellNutriList[i] })),
      ...localTableData.map(({ planCell: pc }, i) => ({
        pc,
        n: tableDataNutriList[i],
      })),
      ...remainingTableData.map((pc) => ({
        pc: pc.planCell,
        n: getNutri([pc.planCell])[0],
      })),
    ]
      .filter(({ pc }) => !isDeleted(pc))
      .map(({ n }) => n);
    return active.length > 0
      ? active.reduce(nutriValueSumReducer)
      : emptyNutriValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planCells, planCellNutriList, localTableData, tableDataNutriList]);

  const persons = usePersonsAsync(false);

  function getPlanCellPersonId(pc: PlanCell): number {
    return pc.indicator === "MealWithIntakes" ? pc.meal.personId : pc.personId;
  }
  function getPlanCellPersonName(pc: PlanCell): string {
    if (pc.indicator === "MealWithIntakes") return pc.meal.personName;
    return findById(persons, pc.personId)?.name ?? "";
  }

  const perPersonNutri = useMemo(() => {
    const map = new Map<number, { name: string; nutri: NutriValues }>();
    const pairs = [
      ...localTableData.map(({ planCell: pc }, i) => ({
        pc,
        nutri: tableDataNutriList[i],
      })),
      ...planCells.map((pc, i) => ({ pc, nutri: planCellNutriList[i] })),
    ];
    pairs
      .filter(({ pc }) => !isDeleted(pc))
      .forEach(({ pc, nutri }) => {
        const pid = getPlanCellPersonId(pc);
        const name = getPlanCellPersonName(pc);
        const existing = map.get(pid);
        if (existing) {
          existing.nutri = nutriValueSumReducer(existing.nutri, nutri);
        } else {
          map.set(pid, { name, nutri: { ...nutri } });
        }
      });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTableData, planCells, tableDataNutriList, planCellNutriList]);

  return {
    purchases,
    foods,
    getNutri,
    planCellNutriList,
    tableDataNutriList,
    totalNutri,
    perPersonNutri,
    getPlanCellPersonName,
  };
}

/**
 * Hook-freie Shell: Zustand liegt oberhalb der Slot-Boundaries. Header, Body
 * und Footer sind eigene Komponenten; ihr Suspense wird pro Slot gefangen und
 * reißt den Drawer nicht mehr nieder.
 */
export function SearchForPlanCellDrawer(props: SearchForPlanCellDrawerProps) {
  const state = useSearchState(props);
  return (
    <>
      <SearchNestedDrawers state={state} />
      <Plan2PlateDrawer
        onClose={props.onClose}
        open={props.open}
        header={
          <PlanCoordinateHeader
            planMode={props.planMode}
            planCellCoordinates={props.planCellCoordinates}
          />
        }
        noScroll
        body={<SearchForPlanCellBody state={state} />}
        footer={
          state.newPlanCellCount > 0 || !state.pristine ? (
            <SearchForPlanCellFooter state={state} />
          ) : undefined
        }
      />
    </>
  );
}

function SearchNestedDrawers({ state }: { state: SearchState }) {
  const {
    props,
    planCells,
    localTableData,
    updatePlanCells,
    updateLocalTableData,
    editResultOnly,
    setEditResultOnly,
    editingSource,
    dishModification,
    foodModification,
    purchaseModification,
    loadPurchase,
    addPlanCell,
    editDishDisclosure,
    editFoodDisclosure,
    editPurchaseDisclosure,
    createItemDisclosure,
    filterDisclosure,
    indicators,
    tags,
    nutriFilter,
    applyNutriFilter,
    countNutriResults,
  } = state;

  return (
    <>
      <NutriFilterDrawer
        open={filterDisclosure.isOpen}
        onClose={filterDisclosure.onClose}
        indicators={indicators}
        tags={tags}
        value={nutriFilter}
        personId={props.planCellCoordinates.personId}
        onApply={applyNutriFilter}
        countResults={countNutriResults}
      />

      {/* EditDishDrawer für Stift-Aktion */}
      <EditDishDrawer
        dish={dishModification}
        onClose={editDishDisclosure.onClose}
        open={editDishDisclosure.isOpen}
        onSubmit={async () => {
          if (editResultOnly) {
            setEditResultOnly(false);
            editDishDisclosure.onClose();
            return;
          }
          if (!editingSource) return;
          const { isNew, index } = editingSource;
          const sourceCell = isNew
            ? convertToNewPlanCellWithIndex(planCells[index])
            : localTableData[index];
          if (!sourceCell) return;
          const newCells = await rebuildPlanCells(localTableData);
          if (newCells.length > 0) {
            if (isNew) {
              updatePlanCells((draft) => {
                draft[index] = newCells[0];
              });
            } else {
              updateLocalTableData((draft) => {
                draft[index].planCell = newCells[0];
              });
            }
          }
        }}
      />

      {/* EditFoodDrawer für Stift-Aktion */}
      <EditFoodDrawer
        allowUnitDeletion
        onClose={editFoodDisclosure.onClose}
        open={editFoodDisclosure.isOpen}
        onSubmit={async () => {
          if (editResultOnly) {
            setEditResultOnly(false);
            editFoodDisclosure.onClose();
            return;
          }
          if (!editingSource) return;
          const { isNew, index } = editingSource;
          const sourceCell = isNew
            ? planCells[index]
            : localTableData[index].planCell;
          if (!sourceCell) return;
          const newCells = await rebuildPlanCells(localTableData);
          if (newCells.length > 0) {
            if (isNew) {
              updatePlanCells((draft) => {
                draft[index] = newCells[0];
              });
            } else {
              updateLocalTableData((draft) => {
                draft[index].planCell = newCells[0];
              });
            }
          }
        }}
        food={foodModification}
      />

      {/* EditPurchaseDrawer für Stift-Aktion */}
      <EditPurchaseDrawer
        onClose={editPurchaseDisclosure.onClose}
        open={editPurchaseDisclosure.isOpen}
        onSubmit={async () => {
          if (editResultOnly) {
            setEditResultOnly(false);
            editPurchaseDisclosure.onClose();
            return;
          }
          if (!editingSource) return;
          const { isNew, index } = editingSource;
          const sourceCell = isNew
            ? planCells[index]
            : localTableData[index].planCell;
          if (!sourceCell) return;
          await loadPurchase();
          const newCells = await rebuildPlanCells(localTableData);
          if (newCells.length > 0) {
            if (isNew) {
              updatePlanCells((draft) => {
                draft[index] = newCells[0];
              });
            } else {
              updateLocalTableData((draft) => {
                draft[index].planCell = newCells[0];
              });
            }
          }
        }}
        purchase={purchaseModification}
        food={foodModification}
      />

      <CreateItemDrawer
        onClose={createItemDisclosure.onClose}
        open={createItemDisclosure.isOpen}
        createItem={(e) => addPlanCell(props.planCellCoordinates, e)}
      />
    </>
  );
}

function SearchForPlanCellBody({ state }: { state: SearchState }) {
  const {
    props,
    planCells,
    updatePlanCells,
    localTableData,
    updateLocalTableData,
    expandedCards,
    setExpandedCards,
    searchTerm,
    setSearchTerm,
    searchResults,
    searchResultCount,
    setEditingSource,
    setEditResultOnly,
    setDishModification,
    setFoodModification,
    setPurchaseModification,
    loadedPlanDishes,
    setLoadedPlanDishes,
    performSearch,
    fetchMoreData,
    mealIndDescription,
    activePlanCellCount,
    isForAll,
    isPlanCellIncomplete,
    addPlanCell,
    deleteNewCell,
    deleteExistingCell,
    hasMeals,
    drawerHistoryContext,
    onConsume,
    editDishDisclosure,
    editFoodDisclosure,
    editPurchaseDisclosure,
    createItemDisclosure,
    filterDisclosure,
    indicators,
    tags,
    nutriFilter,
    clearNutriFilter,
    nutriFilterActive,
    hasSearched,
    setHasSearched,
  } = state;
  const {
    purchases,
    foods,
    planCellNutriList,
    tableDataNutriList,
    getPlanCellPersonName,
  } = useSearchNutri(state);

  // Enter mit Suchbegriff startet die Suche. Ohne Begriff werden Ergebnisse
  // erst über „Ergebnisse anzeigen“ im Filter-Drawer geladen.
  const runSearch = (isNewSearch: boolean) => {
    if (searchTerm.trim() === "") return;
    setHasSearched(true);
    performSearch(isNewSearch);
  };
  const fetchMoreFiltered = () => {
    if (!hasSearched) return;
    fetchMoreData();
  };

  // ─── Pencil: Gericht / Lebensmittel bearbeiten ──────────────────────────────
  const handlePencilClick = async (
    planCell: PlanCell,
    planCellIndex: number,
    isNew: boolean,
  ) => {
    if (planCell.indicator !== "MealWithIntakes") return;
    const meal = planCell.meal;
    if (!isNullIndex(meal.dishId)) {
      const dm = await loadDishModification(meal.dishId);
      setDishModification(dm);
      setEditingSource({ isNew, index: planCellIndex });
      editDishDisclosure.onOpen();
    } else if (!isNullIndex(meal.foodId)) {
      const newFood = findById(foods, meal.foodId);
      setFoodModification(newFood);
      setEditingSource({ isNew, index: planCellIndex });
      editFoodDisclosure.onOpen();
    } else if (!isNullIndex(meal.purchaseId)) {
      const purchase = findById(purchases, meal.purchaseId);
      if (purchase) {
        const newFood = findById(foods, purchase.foodId);
        if (newFood?.fromDish) {
          setFoodModification(newFood);
          setEditingSource({ isNew, index: planCellIndex });
          editFoodDisclosure.onOpen();
          return;
        }
        setFoodModification(newFood);
        setEditingSource({ isNew, index: planCellIndex });
        editPurchaseDisclosure.onOpen();
      }
    }
  };

  // ─── Stift am Suchergebnis: Entität direkt bearbeiten (kein Plan-Cell) ─────
  const openResultEdit = async (result: MealSourceDto) => {
    setEditingSource(null);
    setEditResultOnly(true);
    if (result.indicator === "DISH") {
      if (result.id == null) return;
      const dm = await loadDishModification(result.id);
      setDishModification(dm);
      editDishDisclosure.onOpen();
    } else if (result.indicator === "FOOD") {
      if (result.id == null) return;
      // Food explizit laden – es ist evtl. nicht in `foods` (die nur Foods der
      // aktuellen Plan-Cells enthält) und der Editor bliebe sonst unsichtbar.
      const food = (
        await loadFoodRequest({ path: { foodId: String(result.id) } })
      ).data;
      setFoodModification(food as Food);
      editFoodDisclosure.onOpen();
    } else if (result.indicator === "PURCHASE") {
      if (result.id == null) return;
      const purchase = (
        await getPurchase({ path: { purchaseId: String(result.id) } })
      ).data;
      const food = (
        await loadFoodRequest({ path: { foodId: String(purchase.foodId) } })
      ).data;
      // "Aus Gericht"-Kaufprodukte sind nicht direkt bearbeitbar → Food-Editor öffnen.
      if ((food as Food).fromDish) {
        setFoodModification(food as Food);
        editFoodDisclosure.onOpen();
        return;
      }
      setPurchaseModification(purchase);
      setFoodModification(food);
      editPurchaseDisclosure.onOpen();
    } else {
      setEditResultOnly(false);
    }
  };

  // ─── Inline-Inhalt: Einzelne Zutat (plan mode) ─────────────────────────────
  const renderIngredientRow = (
    mi: MealIngredient,
    miIndex: number,
    planCellIndex: number,
    isNew: boolean,
    isLast: boolean,
    stepInfo?: StepInfo,
    stepIngredient?: StepIngredientModification,
  ) => {
    const isFoodBased = !isNullIndex(mi.foodUnitFoodId);
    const food = isFoodBased ? findById(foods, mi.foodUnitFoodId!) : undefined;
    const purchase = !isFoodBased
      ? findById(purchases, mi.purchaseUnitPurchaseId!)
      : undefined;

    const name = food
      ? formatFoodDescription(food)
      : purchase
        ? formatPurchaseDescription(purchase)
        : mi.foodUnitFoodDescription ||
          mi.purchaseUnitPurchaseDescription ||
          "?";

    type UnitItem = { id: number; description: string; netWeight?: number };
    const unitChoices: UnitItem[] = isFoodBased
      ? food?.foodUnits ?? []
      : purchase?.purchaseUnits ?? [];
    const selectedUnit: UnitItem | undefined = isFoodBased
      ? food?.foodUnits.find((fu) => fu.id === mi.foodUnitId)
      : purchase?.purchaseUnits.find((pu) => pu.id === mi.purchaseUnitId);

    const nutri =
      foods && purchases
        ? nutriValuesFromMealIngredient(mi, foods, purchases)
        : emptyNutriValue;

    const isIncomplete =
      mi.amount <= 0 ||
      (isNullIndex(mi.foodUnitId) && isNullIndex(mi.purchaseUnitId));

    const updateCell = (cb: (cell: MealWithRemoved) => void) => {
      if (isNew) {
        updatePlanCells((draft) =>
          cb(draft[planCellIndex] as unknown as MealWithRemoved),
        );
      } else {
        updateLocalTableData((draft) =>
          cb(draft[planCellIndex].planCell as unknown as MealWithRemoved),
        );
      }
    };

    const updateMI = (cb: (m: MealIngredient) => void) => {
      updateCell((cell) => cb(cell.meal.mealIngredients[miIndex]));
    };

    const handleDeleteIngredient = () => {
      updateCell((cell) => {
        const [removed] = cell.meal.mealIngredients.splice(miIndex, 1);
        if (!removed) return;
        const list = cell.removedIngredients ?? (cell.removedIngredients = []);
        list.forEach((entry) => {
          if (entry.index > miIndex) entry.index -= 1;
        });
        list.push({ index: miIndex, ingredient: removed });
      });
    };

    // Alternativen aus Rezept
    const options: number[] = [];
    if (stepIngredient) {
      if (stepIngredient.foodUnitId) {
        options.push(stepIngredient.foodUnitId);
      }
      stepIngredient.alternatives.forEach((alt) => {
        if (alt.foodUnitId) {
          options.push(alt.foodUnitId);
        }
      });
    }

    const handleSelectAlternative = (targetFood: FoodDto) => {
      if (!targetFood || !targetFood.foodUnits?.length) return;
      const currentFoodUnit = food?.foodUnits.find(
        (fu) => fu.id === mi.foodUnitId,
      );
      const targetFoodUnit = findMatchingUnit(
        currentFoodUnit,
        targetFood.foodUnits,
      );
      updateMI((m) => {
        m.foodUnitId = targetFoodUnit.id;
        m.foodUnitFoodId = targetFood.id;
        m.foodUnitFoodDescription = targetFood.description;
        m.foodUnitDescription = targetFoodUnit.description;
        m.purchaseUnitId = -1;
        m.purchaseUnitPurchaseId = -1;
        m.purchaseUnitDescription = "";
        m.purchaseUnitPurchaseDescription = "";
        m.amount = recalculateAmountByUnitChange(
          currentFoodUnit,
          targetFoodUnit,
          m.amount,
        );
      });
    };

    return (
      <IngredientRow
        key={miIndex}
        isLast={isLast}
        className={isIncomplete ? "bg-protein/5" : undefined}
        title={name}
        alternativeUnitIds={options}
        activeFoodId={mi.foodUnitFoodId}
        onSelectAlternative={(food) => handleSelectAlternative(food)}
        stepInfo={
          stepInfo
            ? { index: stepInfo.stepIndex, text: stepInfo.stepText }
            : undefined
        }
        amount={
          <UnitAmount
            foodUnit={selectedUnit as FoodUnit | undefined}
            units={unitChoices as FoodUnit[]}
            amount={mi.amount}
            onUnitChange={(u) =>
              updateMI((m) => {
                if (isFoodBased) m.foodUnitId = u?.id ?? -1;
                else m.purchaseUnitId = u?.id ?? -1;
                m.amount = recalculateAmountByUnitChange(
                  selectedUnit,
                  u,
                  m.amount,
                );
              })
            }
            onAmountChange={(a) =>
              updateMI((m) => {
                m.amount = a;
              })
            }
          />
        }
        onDelete={handleDeleteIngredient}
        nutri={nutri}
      />
    );
  };

  // ─── Inline-Inhalt: Inaktive Rezept-Zutat (zum Aktivieren) ────────────────
  const renderInactiveIngredientRow = (
    si: SiWithStep,
    planCell: MealWithIntakes,
    planCellIndex: number,
    isNew: boolean,
    removedIndex: number,
  ) => {
    const siFood = si.foodUnitId
      ? getFoodMaybe(si.foodUnitId, foods)
      : undefined;
    const foodUnit = siFood?.foodUnits.find((fu) => fu.id === si.foodUnitId);
    const name = siFood ? formatFoodDescription(siFood) : "?";

    const updateCell = (cb: (cell: MealWithRemoved) => void) => {
      if (isNew) {
        updatePlanCells((draft) =>
          cb(draft[planCellIndex] as unknown as MealWithRemoved),
        );
      } else {
        updateLocalTableData((draft) =>
          cb(draft[planCellIndex].planCell as unknown as MealWithRemoved),
        );
      }
    };

    const handleAdd = () => {
      updateCell((cell) => {
        // Zuvor entfernte Zutat an ihrer ursprünglichen Position und mit ihrer
        // Menge wiederherstellen (analog Verzehr/Kochdialog).
        if (removedIndex >= 0) {
          const list = cell.removedIngredients ?? [];
          const [entry] = list.splice(removedIndex, 1);
          if (!entry) return;
          const insertIndex = Math.max(
            0,
            Math.min(entry.index, cell.meal.mealIngredients.length),
          );
          cell.meal.mealIngredients.splice(insertIndex, 0, entry.ingredient);
          list.forEach((removed) => {
            if (removed.index >= insertIndex) removed.index += 1;
          });
          return;
        }
        // Neue Rezept-Zutat hinzufügen.
        cell.meal.mealIngredients.push({
          id: -1,
          amount: 0,
          foodUnitDescription: foodUnit?.description || "",
          foodUnitFoodDescription: siFood?.description || "",
          foodUnitFoodId: siFood?.id ?? -1,
          foodUnitId: si.foodUnitId ?? -1,
          mealId: planCell.meal.id,
          purchaseUnitDescription: "",
          purchaseUnitId: -1,
          purchaseUnitPurchaseDescription: "",
          purchaseUnitPurchaseId: -1,
          stepIndex: si.stepPos,
          stepText: si.stepText || undefined,
        });
      });
    };

    return (
      <IngredientRestoreRow
        key={`inactive-${si.stepPos}-${si.foodUnitId}`}
        name={name}
        onRestore={handleAdd}
      />
    );
  };

  // ─── Inline-Inhalt: Gericht mit Rezept (Plan-Modus) ────────────────────────
  const renderDishInlineContent = (
    planCell: MealWithIntakes,
    planCellIndex: number,
    isNew: boolean,
    dish: DishModification,
  ) => {
    const ingredients = planCell.meal.mealIngredients;
    const removedIngredients =
      (planCell as MealWithRemoved).removedIngredients ?? [];

    const allSis: SiWithStep[] = (dish.steps || []).flatMap((s) =>
      s.ingredients.map((si) => ({
        ...si,
        stepPos: s.position ?? 0,
        stepText: s.text || null,
      })),
    );

    function sameFoodAsSi(mi: MealIngredient, si: SiWithStep): boolean {
      return (
        si.foodUnitId === mi.foodUnitId ||
        si.alternatives?.some((alt) => alt.foodUnitId === mi.foodUnitId) ||
        (si.foodUnitId !== undefined &&
          getFoodMaybe(si.foodUnitId, foods)?.id === mi.foodUnitFoodId) ||
        si.alternatives?.some(
          (alt) =>
            alt.foodUnitId &&
            getFoodMaybe(alt.foodUnitId, foods)?.id === mi.foodUnitFoodId,
        )
      );
    }

    function findMatchingSiForMI(mi: MealIngredient): SiWithStep | undefined {
      return allSis.find((si) => {
        if (mi.stepIndex != null && mi.stepIndex !== si.stepPos) return false;
        return (
          si.foodUnitId === mi.foodUnitId ||
          sameFoodAsSi(mi, si) ||
          si.alternatives?.some((alt) => alt.foodUnitId === mi.foodUnitId)
        );
      });
    }

    function siIsCovered(si: SiWithStep): boolean {
      return ingredients.some((mi) => {
        if (mi.stepIndex != null && mi.stepIndex !== si.stepPos) return false;
        return (
          si.foodUnitId === mi.foodUnitId ||
          sameFoodAsSi(mi, si) ||
          si.alternatives?.some((alt) => alt.foodUnitId === mi.foodUnitId)
        );
      });
    }

    const inactiveSis = allSis.filter((si) => !siIsCovered(si));

    // Duplikate erkennen → Schritt-Info anzeigen
    const foodIdCounts = new Map<number, number>();
    ingredients.forEach((mi) => {
      if (!isNullIndex(mi.foodUnitFoodId)) {
        foodIdCounts.set(
          mi.foodUnitFoodId,
          (foodIdCounts.get(mi.foodUnitFoodId) ?? 0) + 1,
        );
      }
    });

    // Nach Schritt-Reihenfolge anzeigen (Originalindex für Updates behalten).
    const orderedIngredients = ingredients
      .map((mi, index) => ({ mi, index }))
      .sort(
        (a, b) =>
          (a.mi.stepIndex ?? Number.MAX_SAFE_INTEGER) -
          (b.mi.stepIndex ?? Number.MAX_SAFE_INTEGER),
      );

    return (
      <div className="flex flex-col">
        {orderedIngredients.map(({ mi, index }, i) => {
          const isDuplicate =
            !isNullIndex(mi.foodUnitFoodId) &&
            (foodIdCounts.get(mi.foodUnitFoodId) ?? 0) > 1 &&
            mi.stepIndex != null;
          const stepInfo: StepInfo | undefined = isDuplicate
            ? { stepIndex: mi.stepIndex!, stepText: mi.stepText }
            : undefined;
          const matchingSi = findMatchingSiForMI(mi);
          return renderIngredientRow(
            mi,
            index,
            planCellIndex,
            isNew,
            i === orderedIngredients.length - 1,
            stepInfo,
            matchingSi,
          );
        })}
        {inactiveSis.map((si) => {
          // Wurde die Zutat vorher entfernt? Dann an ursprünglicher Stelle und
          // mit ursprünglicher Menge wiederherstellen.
          const removedIndex = removedIngredients.findIndex((removed) => {
            const mi = removed.ingredient;
            if (mi.stepIndex != null && mi.stepIndex !== si.stepPos)
              return false;
            return (
              si.foodUnitId === mi.foodUnitId ||
              sameFoodAsSi(mi, si) ||
              si.alternatives?.some((alt) => alt.foodUnitId === mi.foodUnitId)
            );
          });
          return renderInactiveIngredientRow(
            si,
            planCell,
            planCellIndex,
            isNew,
            removedIndex,
          );
        })}
        {ingredients.length === 0 && inactiveSis.length === 0 && (
          <div className="px-3 py-3 text-xs text-ink-muted">Keine Zutaten</div>
        )}
      </div>
    );
  };

  // ─── Inline-Inhalt: JustIntake ──────────────────────────────────────────────
  const renderJustIntakeContent = (
    planCell: JustIntake,
    planCellIndex: number,
    isNew: boolean,
  ) => {
    const purchase = findById(purchases, planCell.purchaseUnitPurchaseId);
    const purchaseUnit = purchase?.purchaseUnits.find(
      (pu) => pu.id === planCell.purchaseUnitId,
    );
    const isIncomplete =
      planCell.amount <= 0 || isNullIndex(planCell.purchaseUnitId);
    const nutri = nutriValuesFromIntake(planCell, purchases);

    const name = purchase
      ? formatPurchaseDescription(purchase)
      : planCell.purchaseUnitPurchaseDescription;

    const updateIntake = (cb: (pc: JustIntake) => void) => {
      if (isNew) {
        updatePlanCells((draft) => cb(draft[planCellIndex] as JustIntake));
      } else {
        updateLocalTableData((draft) =>
          cb(draft[planCellIndex].planCell as JustIntake),
        );
      }
    };

    return (
      <div
        className={
          "flex flex-col gap-2 px-3 py-3" +
          (isIncomplete ? " bg-protein/5" : "")
        }
      >
        <span className="text-sm leading-snug">{name}</span>

        <UnitAmount
          foodUnit={purchaseUnit as FoodUnit | undefined}
          units={(purchase?.purchaseUnits || []) as FoodUnit[]}
          amount={planCell.amount}
          onUnitChange={(u) =>
            updateIntake((pc) => {
              pc.purchaseUnitId = u?.id ?? -1;
              pc.amount = recalculateAmountByUnitChange(
                purchaseUnit,
                u,
                pc.amount,
              );
            })
          }
          onAmountChange={(a) =>
            updateIntake((pc) => {
              pc.amount = a;
            })
          }
          className="flex-1 min-w-0"
        />

        <NutriRow nutri={nutri} />
      </div>
    );
  };

  // ─── Inline-Inhalt: Intake-Zeile (intake mode, MealWithIntakes) ─────────────
  const renderIntakeRow = (
    intake: IntakeWithEditedFlags,
    intakeIndex: number,
    planCellIndex: number,
    isNew: boolean,
    isLast: boolean,
  ) => {
    if (isDeleted(intake)) return null;
    const purchase = findById(purchases, intake.purchaseUnitPurchaseId);
    const purchaseUnit = purchase?.purchaseUnits.find(
      (pu) => pu.id === intake.purchaseUnitId,
    );
    const isIncomplete =
      intake.amount <= 0 || isNullIndex(intake.purchaseUnitId);
    const nutri = nutriValuesFromIntake(intake, purchases);

    const name = purchase
      ? formatPurchaseDescription(purchase)
      : intake.purchaseUnitPurchaseDescription;

    const updateIntake = (cb: (i: IntakeWithEditedFlags) => void) => {
      if (isNew) {
        updatePlanCells((draft) => {
          const mwi = draft[planCellIndex] as MealWithIntakes;
          cb(mwi.intakes![intakeIndex] as IntakeWithEditedFlags);
        });
      } else {
        updateLocalTableData((draft) => {
          const mwi = draft[planCellIndex].planCell as MealWithIntakes;
          cb(mwi.intakes![intakeIndex] as IntakeWithEditedFlags);
        });
      }
    };

    const rowClass =
      "flex flex-col gap-2 px-3 py-3" +
      (!isLast ? " border-b border-line/40" : "") +
      (isIncomplete ? " bg-protein/5" : "");

    return (
      <div key={intakeIndex} className={rowClass}>
        <span className="text-sm leading-snug">{name}</span>

        <UnitAmount
          foodUnit={purchaseUnit as FoodUnit | undefined}
          units={(purchase?.purchaseUnits || []) as FoodUnit[]}
          amount={intake.amount}
          onUnitChange={(u) =>
            updateIntake((i) => {
              i.purchaseUnitId = u?.id ?? -1;
              i.amount = recalculateAmountByUnitChange(
                purchaseUnit,
                u,
                i.amount,
              );
              i.edited = true;
            })
          }
          onAmountChange={(a) =>
            updateIntake((i) => {
              i.amount = a;
              i.edited = true;
            })
          }
          className="flex-1 min-w-0"
        />

        <NutriRow nutri={nutri} />
      </div>
    );
  };

  // ─── Inline-Inhalt je nach Typ ─────────────────────────────────────────────
  const renderInlineContent = (
    planCell: PlanCell,
    planCellIndex: number,
    isNew: boolean,
  ) => {
    if (planCell.indicator === "JustIntake") {
      return renderJustIntakeContent(planCell, planCellIndex, isNew);
    }

    if (props.planMode === "plan") {
      const ingredients = planCell.meal.mealIngredients;

      if (!isNullIndex(planCell.meal.dishId)) {
        const dish = loadedPlanDishes[planCell.meal.dishId];
        if (dish) {
          return renderDishInlineContent(planCell, planCellIndex, isNew, dish);
        }
      }

      const foodIdCounts = new Map<number, number>();
      ingredients.forEach((mi) => {
        if (!isNullIndex(mi.foodUnitFoodId)) {
          foodIdCounts.set(
            mi.foodUnitFoodId,
            (foodIdCounts.get(mi.foodUnitFoodId) ?? 0) + 1,
          );
        }
      });

      const orderedIngredients = ingredients
        .map((mi, index) => ({ mi, index }))
        .sort(
          (a, b) =>
            (a.mi.stepIndex ?? Number.MAX_SAFE_INTEGER) -
            (b.mi.stepIndex ?? Number.MAX_SAFE_INTEGER),
        );

      return (
        <div className="flex flex-col">
          {orderedIngredients.map(({ mi, index }, i) => {
            const isDuplicate =
              !isNullIndex(mi.foodUnitFoodId) &&
              (foodIdCounts.get(mi.foodUnitFoodId) ?? 0) > 1 &&
              mi.stepIndex != null;
            const stepInfo: StepInfo | undefined = isDuplicate
              ? { stepIndex: mi.stepIndex!, stepText: mi.stepText }
              : undefined;
            return renderIngredientRow(
              mi,
              index,
              planCellIndex,
              isNew,
              i === orderedIngredients.length - 1,
              stepInfo,
            );
          })}
          {ingredients.length === 0 && (
            <div className="px-3 py-3 text-xs text-ink-muted">
              Keine Zutaten
            </div>
          )}
        </div>
      );
    }

    // intake mode – MealWithIntakes
    const intakes = planCell.intakes ?? [];
    const orderedIntakes = intakes
      .map((intake, idx) => ({ intake, idx }))
      .filter(({ intake }) => !isDeleted(intake))
      .sort(
        (a, b) =>
          (a.intake.stepIndex ?? Number.MAX_SAFE_INTEGER) -
          (b.intake.stepIndex ?? Number.MAX_SAFE_INTEGER),
      );
    if (orderedIntakes.length === 0) {
      return (
        <div className="px-3 py-3 text-xs text-ink-muted">
          Noch kein Verzehr erfasst
        </div>
      );
    }
    return (
      <div className="flex flex-col">
        {orderedIntakes.map(({ intake, idx }, i) =>
          renderIntakeRow(
            intake,
            idx,
            planCellIndex,
            isNew,
            i === orderedIntakes.length - 1,
          ),
        )}
      </div>
    );
  };

  // ─── PlanCell-Karte (aufklappbar) ───────────────────────────────────────────
  const renderPlanCellCard = (
    planCell: PlanCell,
    nutri: typeof emptyNutriValue,
    cardKey: string,
    planCellIndex: number,
    isNew: boolean,
  ) => {
    if (isDeleted(planCell)) return null;
    const description = formatPlanCellDescription(planCell, purchases);
    const incomplete = isPlanCellIncomplete(planCell);
    const isExpanded = expandedCards.has(cardKey);

    const toggleExpand = () => {
      const isCurrentlyExpanded = expandedCards.has(cardKey);
      // Rezept-Daten laden, wenn Gericht-Karte aufgeklappt wird
      if (
        !isCurrentlyExpanded &&
        props.planMode === "plan" &&
        planCell.indicator === "MealWithIntakes" &&
        !isNullIndex(planCell.meal.dishId)
      ) {
        const dishId = planCell.meal.dishId;
        if (!loadedPlanDishes[dishId]) {
          loadDishModification(dishId).then((dish) => {
            setLoadedPlanDishes((prev) => ({ ...prev, [dishId]: dish }));
          });
        }
      }
      setExpandedCards((prev) => {
        const next = new Set(prev);
        if (next.has(cardKey)) next.delete(cardKey);
        else next.add(cardKey);
        return next;
      });
    };

    const hasPencil =
      planCell.indicator === "MealWithIntakes" &&
      (!isNullIndex(planCell.meal.dishId) ||
        !isNullIndex(planCell.meal.foodId) ||
        !isNullIndex(planCell.meal.purchaseId));

    return (
      <PlanCellCard
        key={cardKey}
        description={description}
        kcal={nutri.kcal}
        nutri={nutri}
        incomplete={incomplete}
        expanded={isExpanded}
        personName={isForAll ? getPlanCellPersonName(planCell) : undefined}
        hasPencil={hasPencil}
        onToggleExpand={toggleExpand}
        onEdit={() => handlePencilClick(planCell, planCellIndex, isNew)}
        onDelete={() =>
          isNew
            ? deleteNewCell(planCellIndex)
            : deleteExistingCell(planCellIndex)
        }
      >
        {renderInlineContent(planCell, planCellIndex, isNew)}
      </PlanCellCard>
    );
  };

  return (
    <div className="flex flex-col w-full flex-1 min-h-0 h-full">
      {!props.hideSearch && (
        <div className="relative z-10 -mx-4 -mt-4 flex flex-col gap-2 bg-surface-card px-4 pt-2 pb-2 shadow-down">
          <div className="flex items-center gap-2">
            <SearchInput
              value={searchTerm}
              dataCy="search-for-food-input"
              setSearchTerm={setSearchTerm}
              performSearch={runSearch}
              placeholder={mealIndDescription + " suchen"}
              filterActive={nutriFilterActive}
              noWFull
            />
            <SmallIconButton
              icon="atHorizontalSliderDots"
              label="Nährwerte filtern"
              data-cy="nutri-filter-button"
              active={nutriFilterActive}
              onClick={filterDisclosure.onOpen}
            />
            <AddButton onClick={createItemDisclosure.onOpen} />
            {props.planMode == "intake" &&
              isTodayOrPrevious(props.planCellCoordinates.date) &&
              hasMeals &&
              !!props.planCellCoordinates.personId && (
                <FastIntakeButton
                  onClick={async () => {
                    if (!!props.planCellCoordinates.personId) {
                      await drawerHistoryContext.clearHistory();
                      onConsume(
                        props.planCellCoordinates.date,
                        props.planCellCoordinates.mealInd,
                        undefined,
                        props.planCellCoordinates.personId,
                      );
                    }
                  }}
                />
              )}
          </div>
          {nutriFilterActive && (
            <div className="scroll-container flex items-center gap-1.5 overflow-x-auto">
              <FilterPill
                label="Art"
                value={
                  indicators.length > 0
                    ? indicators.map((i) => mealIndicatorLabels[i]).join(", ")
                    : undefined
                }
                onRemove={() => clearNutriFilter("indicators")}
                onOpen={filterDisclosure.onOpen}
              />
              <FilterPill
                label="Tags"
                value={tags.length > 0 ? String(tags.length) : undefined}
                onRemove={() => clearNutriFilter("tags")}
                onOpen={filterDisclosure.onOpen}
              />
              <FilterPill
                label="Kcal"
                value={formatNutriFilterRange(
                  nutriFilter.minKcal,
                  nutriFilter.maxKcal,
                )}
                onRemove={() => clearNutriFilter("kcal")}
                onOpen={filterDisclosure.onOpen}
              />
              <FilterPill
                label="Fett"
                value={formatNutriFilterRange(
                  nutriFilter.minFat,
                  nutriFilter.maxFat,
                )}
                onRemove={() => clearNutriFilter("fat")}
                onOpen={filterDisclosure.onOpen}
              />
              <FilterPill
                label="KH"
                value={formatNutriFilterRange(
                  nutriFilter.minCarbs,
                  nutriFilter.maxCarbs,
                )}
                onRemove={() => clearNutriFilter("carbs")}
                onOpen={filterDisclosure.onOpen}
              />
              <FilterPill
                label="Protein"
                value={formatNutriFilterRange(
                  nutriFilter.minProtein,
                  nutriFilter.maxProtein,
                )}
                onRemove={() => clearNutriFilter("protein")}
                onOpen={filterDisclosure.onOpen}
              />
            </div>
          )}
        </div>
      )}

      {/* Einheitlicher Scroll-Container: Cards + Ergebnisse */}
      <div
        id="search-for-plan-cell-drawer-scroll-stack"
        className="pt-2 flex-1 min-h-0 overflow-y-auto w-full"
      >
        <div className="flex flex-col gap-2 pb-2">
          {/* Cards – expandieren frei, kein eigenes max-h */}
          {activePlanCellCount > 0 && (
            <div className="flex flex-col gap-2">
              {localTableData.map((pc, i) =>
                renderPlanCellCard(
                  pc.planCell,
                  tableDataNutriList[i],
                  `existing-${i}`,
                  i,
                  false,
                ),
              )}
              {planCells.map((pc, i) =>
                renderPlanCellCard(
                  pc,
                  planCellNutriList[i],
                  `new-${i}`,
                  i,
                  true,
                ),
              )}
            </div>
          )}

          {!props.hideSearch && (
            <>
              <div className="w-full h-2 mt-2 border-t border-t-line border-solid" />
              <InfiniteScroll
                scrollableTarget="search-for-plan-cell-drawer-scroll-stack"
                dataLength={searchResults.length}
                next={fetchMoreFiltered}
                hasMore={
                  hasSearched && searchResults.length < searchResultCount
                }
                loader={<h4>Loading…</h4>}
                style={{ width: "100%" }}
              >
                <div className="flex flex-col w-full gap-2">
                  {searchResults.map((mealSource) => (
                    <DetailSearchResult
                      key={getMealSourceKey(mealSource)}
                      extraTags={mealSource.extraTags}
                      tags={mealSource.tags}
                      title={mealSource.description}
                      resultType={computeResultType(mealSource)}
                      recent={isRecent(mealSource)}
                      purchaseRelevant={mealSource.purchaseRelevant}
                      nutriValues={mealSource}
                      showNumbers={
                        mealSource.indicator == "DISH" ||
                        mealSource.indicator == "MEAL" ||
                        mealSource.indicator == "MEALS_FOR_DAY"
                      }
                      onClick={async () => {
                        await addPlanCell(
                          props.planCellCoordinates,
                          mealSource,
                        );
                      }}
                      onEdit={
                        mealSource.indicator == "DISH" ||
                        mealSource.indicator == "FOOD" ||
                        mealSource.indicator == "PURCHASE"
                          ? () => openResultEdit(mealSource)
                          : undefined
                      }
                    />
                  ))}
                </div>
              </InfiniteScroll>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SearchForPlanCellFooter({ state }: { state: SearchState }) {
  const { isForAll, mealIndDescription, saveLabel, handleSave } = state;
  const { perPersonNutri, totalNutri } = useSearchNutri(state);

  return (
    <div className="flex flex-col gap-2 w-full shrink-0 pt-2">
      {isForAll ? (
        [...perPersonNutri.entries()].map(([pid, { name, nutri }]) => (
          <Card key={pid} className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-sm">
                Gesamt {mealIndDescription}{" "}
                <span className="text-teal">{name}</span>
              </span>
              <span className="text-gold font-bold text-sm">
                {Math.round(nutri.kcal)} kcal
              </span>
            </div>
            <MacroBar
              fat={nutri.fat}
              carbs={nutri.carbs}
              protein={nutri.protein}
              kcal={nutri.kcal}
              showLabels
              hideKcal
            />
          </Card>
        ))
      ) : (
        <Card className="p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-sm">
              Gesamt {mealIndDescription}
            </span>
            <span className="text-gold font-bold text-sm">
              {Math.round(totalNutri.kcal)} kcal
            </span>
          </div>
          <MacroBar
            fat={totalNutri.fat}
            carbs={totalNutri.carbs}
            protein={totalNutri.protein}
            kcal={totalNutri.kcal}
            showLabels
            hideKcal
          />
        </Card>
      )}

      <Button
        dataCy="plan-save-button"
        className="w-full rounded-card bg-teal text-white py-3.5 font-semibold text-sm flex items-center justify-center gap-2 active:opacity-80 transition-opacity"
        onClick={handleSave}
      >
        <span>✓</span>
        <span>{saveLabel}</span>
      </Button>
    </div>
  );
}
